import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { signOut, updatePassword } from "firebase/auth";
import { auth, database } from "../firebase/firebase";
import { ref, onValue, update } from "firebase/database";
import QRCode from 'qrcode'; // Add this import
import "../styles/main.css";

export default function Settings() {

  const navigate = useNavigate();

  // Company Details State
  const [company, setCompany] = useState({
    name: "",
    phone: "",
    address: "",
    gstin: "",
    email: "",
    website: "",
    logo: "",
    state: "",
    stateCode: ""
  });

  // Bank Details State
  const [bankDetails, setBankDetails] = useState({
    bankName: "",
    accountNo: "",
    ifsc: "",
    holderName: "",
    branch: ""
  });

  // UPI Details State
  const [upiDetails, setUpiDetails] = useState({
    upiId: "",
    upiApp: "",
    showQR: true
  });

  // QR Code Preview State - NEW
  const [qrCodePreview, setQrCodePreview] = useState(null);
  const [isGeneratingQR, setIsGeneratingQR] = useState(false);

  // Settings State
  const [financialYear, setFinancialYear] = useState("2025-26");
  const [pdfShareOption, setPdfShareOption] = useState("share");
  const [printFormat, setPrintFormat] = useState("standard");
  const [billFormat, setBillFormat] = useState("standard");
  const [billSize, setBillSize] = useState("a4");
  const [notificationEnabled, setNotificationEnabled] = useState(true);
  const [darkMode, setDarkMode] = useState(false);
  const [autoBackup, setAutoBackup] = useState(true);

  // Password Change State
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState("");

  // Financial Year Change State
  const [showFinancialYearModal, setShowFinancialYearModal] = useState(false);
  const [selectedFinancialYear, setSelectedFinancialYear] = useState("2025-26");
  const [financialYearError, setFinancialYearError] = useState("");
  const [financialYearSuccess, setFinancialYearSuccess] = useState("");

  // Add Company Modal State
  const [showAddCompanyModal, setShowAddCompanyModal] = useState(false);
  const [newCompany, setNewCompany] = useState({
    name: "",
    phone: "",
    address: "",
    gstin: "",
    email: "",
    website: "",
    logo: "",
    state: "",
    stateCode: ""
  });

  // Edit Mode State
  const [isEditing, setIsEditing] = useState(false);
  const [editedCompany, setEditedCompany] = useState({});

  // Bank Details Edit State
  const [isEditingBank, setIsEditingBank] = useState(false);
  const [editedBankDetails, setEditedBankDetails] = useState({});

  // UPI Details Edit State
  const [isEditingUPI, setIsEditingUPI] = useState(false);
  const [editedUPIDetails, setEditedUPIDetails] = useState({});

  // Generate QR Code Preview - NEW
  const generateQRPreview = async (upiId, amount = 100, name = company.name) => {
    if (!upiId) {
      setQrCodePreview(null);
      return;
    }

    setIsGeneratingQR(true);
    try {
      // Clean up UPI ID
      const cleanUpiId = upiId.trim().toLowerCase();
      
      // Format UPI URL for preview (using sample amount of ₹100)
      const params = new URLSearchParams({
        pa: cleanUpiId,
        pn: name.substring(0, 25) || 'Your Store',
        am: amount.toFixed(2),
        tn: 'Sample Payment',
        cu: 'INR'
      });
      
      const upiUrl = `upi://pay?${params.toString()}`;
      
      // Generate QR code
      const qrDataUrl = await QRCode.toDataURL(upiUrl, {
        errorCorrectionLevel: 'H',
        margin: 2,
        width: 250,
        color: {
          dark: '#000000',
          light: '#ffffff'
        }
      });
      
      setQrCodePreview(qrDataUrl);
    } catch (error) {
      console.error('Error generating QR preview:', error);
      setQrCodePreview(null);
    } finally {
      setIsGeneratingQR(false);
    }
  };

  // Load Company Details from Firebase
  useEffect(() => {
    const companyRef = ref(database, "company");
    onValue(companyRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        setCompany(data);
        setEditedCompany(data);
      }
    });

    // Load Bank Details from Firebase
    const bankRef = ref(database, "bankDetails");
    onValue(bankRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        setBankDetails(data);
        setEditedBankDetails(data);
      } else {
        const defaultBank = {
          bankName: "",
          accountNo: "",
          ifsc: "",
          holderName: "",
          branch: ""
        };
        setBankDetails(defaultBank);
        setEditedBankDetails(defaultBank);
      }
    });

    // Load UPI Details from Firebase
    const upiRef = ref(database, "upiDetails");
    onValue(upiRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        setUpiDetails(data);
        setEditedUPIDetails(data);
        // Generate QR preview when UPI details are loaded
        if (data.upiId) {
          generateQRPreview(data.upiId, 100, company.name);
        }
      } else {
        const defaultUPI = {
          upiId: "",
          upiApp: "",
          showQR: true
        };
        setUpiDetails(defaultUPI);
        setEditedUPIDetails(defaultUPI);
      }
    });

    // Load Settings from Firebase
    const settingsRef = ref(database, "settings");
    onValue(settingsRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        setFinancialYear(data.financialYear || "2025-26");
        setSelectedFinancialYear(data.financialYear || "2025-26");
        setPdfShareOption(data.pdfShareOption || "share");
        setPrintFormat(data.printFormat || "standard");
        setBillFormat(data.billFormat || "standard");
        setBillSize(data.billSize || "a4");
        setNotificationEnabled(data.notificationEnabled ?? true);
        setDarkMode(data.darkMode || false);
        setAutoBackup(data.autoBackup ?? true);
      }
    });
  }, []);

  // Generate QR preview when company name or UPI ID changes
  useEffect(() => {
    if (upiDetails.upiId) {
      generateQRPreview(upiDetails.upiId, 100, company.name);
    }
  }, [upiDetails.upiId, company.name]);

  // Handle Company Edit
  const handleEditToggle = () => {
    if (isEditing) {
      setIsEditing(false);
      setEditedCompany(company);
    } else {
      setIsEditing(true);
      setEditedCompany({ ...company });
    }
  };

  // Handle Bank Details Edit
  const handleBankEditToggle = () => {
    if (isEditingBank) {
      setIsEditingBank(false);
      setEditedBankDetails(bankDetails);
    } else {
      setIsEditingBank(true);
      setEditedBankDetails({ ...bankDetails });
    }
  };

  // Handle UPI Details Edit
  const handleUPIEditToggle = () => {
    if (isEditingUPI) {
      setIsEditingUPI(false);
      setEditedUPIDetails(upiDetails);
    } else {
      setIsEditingUPI(true);
      setEditedUPIDetails({ ...upiDetails });
    }
  };

  // Handle Input Change
  const handleCompanyChange = (field, value) => {
    setEditedCompany(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle Bank Input Change
  const handleBankChange = (field, value) => {
    setEditedBankDetails(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle UPI Input Change
  const handleUPIChange = (field, value) => {
    setEditedUPIDetails(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Generate QR preview in real-time when UPI ID changes
    if (field === 'upiId' && value) {
      generateQRPreview(value, 100, company.name);
    } else if (field === 'upiId' && !value) {
      setQrCodePreview(null);
    }
  };

  // Handle New Company Input Change
  const handleNewCompanyChange = (field, value) => {
    setNewCompany(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Save Company Details
  const saveCompanyDetails = async () => {
    try {
      await update(ref(database, "company"), editedCompany);
      setCompany(editedCompany);
      setIsEditing(false);
      alert("Company details updated successfully!");
    } catch (error) {
      alert("Error updating company details: " + error.message);
    }
  };

  // Save Bank Details
  const saveBankDetails = async () => {
    try {
      await update(ref(database, "bankDetails"), editedBankDetails);
      setBankDetails(editedBankDetails);
      setIsEditingBank(false);
      alert("Bank details updated successfully!");
    } catch (error) {
      alert("Error updating bank details: " + error.message);
    }
  };

  // Save UPI Details
  const saveUPIDetails = async () => {
    try {
      await update(ref(database, "upiDetails"), editedUPIDetails);
      setUpiDetails(editedUPIDetails);
      setIsEditingUPI(false);
      
      // Generate QR preview after save
      if (editedUPIDetails.upiId) {
        generateQRPreview(editedUPIDetails.upiId, 100, company.name);
      }
      
      alert("UPI details updated successfully!");
    } catch (error) {
      alert("Error updating UPI details: " + error.message);
    }
  };

  // Add New Company
  const addCompany = async () => {
    if (!newCompany.name) {
      alert("Please enter company name");
      return;
    }

    try {
      await update(ref(database, "company"), newCompany);
      setCompany(newCompany);
      setShowAddCompanyModal(false);
      setNewCompany({
        name: "",
        phone: "",
        address: "",
        gstin: "",
        email: "",
        website: "",
        logo: "",
        state: "",
        stateCode: ""
      });
      alert("Company details added successfully!");
    } catch (error) {
      alert("Error adding company details: " + error.message);
    }
  };

  // Save Settings
  const saveSettings = async () => {
    try {
      await update(ref(database, "settings"), {
        financialYear,
        pdfShareOption,
        printFormat,
        billFormat,
        billSize,
        notificationEnabled,
        darkMode,
        autoBackup
      });
      alert("Settings updated successfully!");
    } catch (error) {
      alert("Error updating settings: " + error.message);
    }
  };

  // Change Password
  const handlePasswordChange = async () => {
    setPasswordError("");
    setPasswordSuccess("");

    if (!currentPassword) {
      setPasswordError("Please enter current password");
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError("New passwords do not match");
      return;
    }

    if (newPassword.length < 6) {
      setPasswordError("Password must be at least 6 characters");
      return;
    }

    try {
      const user = auth.currentUser;
      
      if (!user) {
        setPasswordError("No user logged in");
        return;
      }

      await updatePassword(user, newPassword);
      
      setPasswordSuccess("Password changed successfully!");
      setTimeout(() => {
        setShowPasswordModal(false);
        setCurrentPassword("");
        setNewPassword("");
        setConfirmPassword("");
        setPasswordSuccess("");
      }, 1500);
      
    } catch (error) {
      console.error("Password change error:", error);
      
      switch (error.code) {
        case 'auth/requires-recent-login':
          setPasswordError("Please log out and log in again before changing password");
          break;
        case 'auth/weak-password':
          setPasswordError("Password should be at least 6 characters");
          break;
        default:
          setPasswordError(error.message);
      }
    }
  };

  // Change Financial Year
  const handleFinancialYearChange = async () => {
    setFinancialYearError("");
    setFinancialYearSuccess("");

    if (!selectedFinancialYear) {
      setFinancialYearError("Please select a financial year");
      return;
    }

    try {
      await update(ref(database, "settings"), {
        financialYear: selectedFinancialYear
      });

      setFinancialYear(selectedFinancialYear);
      
      setFinancialYearSuccess(`Financial year changed to ${selectedFinancialYear} successfully!`);
      setTimeout(() => {
        setShowFinancialYearModal(false);
        setFinancialYearSuccess("");
      }, 1500);
      
    } catch (error) {
      setFinancialYearError("Error changing financial year: " + error.message);
    }
  };

  // Logout
  const logout = async () => {
    if (window.confirm("Are you sure you want to logout?")) {
      await signOut(auth);
      navigate("/login");
    }
  };

  // Generate Financial Years
  const generateFinancialYears = () => {
    const years = [];
    const startYear = 2020;
    const endYear = 2035;
    
    for (let year = startYear; year < endYear; year++) {
      const nextYear = year + 1;
      const yearString = `${year}-${nextYear.toString().slice(-2)}`;
      years.push({
        value: yearString,
        label: `${yearString} (1 Apr ${year} - 31 Mar ${nextYear})`,
        startDate: `01 Apr ${year}`,
        endDate: `31 Mar ${nextYear}`
      });
    }
    return years;
  };

  const financialYearOptions = generateFinancialYears();

  // Bill Size Options
  const billSizeOptions = [
    { value: "a4", label: "A4 (Standard) - 210mm x 297mm" },
    { value: "a5", label: "A5 (Half Letter) - 148mm x 210mm" },
    { value: "thermal-80", label: "Thermal 80mm - 80mm width" },
    { value: "thermal-58", label: "Thermal 58mm - 58mm width" },
    { value: "letter", label: "Letter - 216mm x 279mm" },
    { value: "legal", label: "Legal - 216mm x 356mm" }
  ];

  // Print Format Options
  const printFormats = [
    { value: "standard", label: "Standard Format" },
    { value: "compact", label: "Compact Format" },
    { value: "detailed", label: "Detailed Format" },
    { value: "minimal", label: "Minimal Format" },
    { value: "gst", label: "GST Invoice Format" }
  ];

  // Bill Format Options
  const billFormats = [
    { value: "standard", label: "Standard Bill" },
    { value: "tax-invoice", label: "Tax Invoice" },
    { value: "cash-memo", label: "Cash Memo" },
    { value: "estimate", label: "Estimate/Quotation" },
    { value: "proforma", label: "Proforma Invoice" }
  ];

  // Check if company details exist
  const hasCompanyDetails = company.name || company.phone || company.address || company.gstin || company.email || company.website || company.state;

  // Check if bank details exist
  const hasBankDetails = bankDetails.bankName || bankDetails.accountNo || bankDetails.ifsc;

  // Check if UPI details exist
  const hasUPIDetails = upiDetails.upiId;

  return (
    <div className="mobile-app">

      {/* HEADER */}
      <div className="page-header">
        <h2>Settings</h2>
      </div>

      {/* COMPANY DETAILS SECTION */}
      <div className="section-card">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "15px" }}>
          <h3 style={{ margin: 0 }}>Company Details</h3>
          {hasCompanyDetails ? (
            <button
              onClick={handleEditToggle}
              style={{
                background: isEditing ? "#ff4444" : "#5a60ea",
                color: "white",
                border: "none",
                padding: "5px 15px",
                borderRadius: "20px",
                cursor: "pointer",
                fontSize: "12px"
              }}
            >
              {isEditing ? "Cancel" : "Edit"}
            </button>
          ) : (
            <button
              onClick={() => setShowAddCompanyModal(true)}
              style={{
                background: "#4CAF50",
                color: "white",
                border: "none",
                padding: "5px 15px",
                borderRadius: "20px",
                cursor: "pointer",
                fontSize: "12px"
              }}
            >
              + Add Company
            </button>
          )}
        </div>

        {hasCompanyDetails ? (
          isEditing ? (
            // Edit Mode
            <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
              <input
                type="text"
                placeholder="Company Name"
                value={editedCompany.name || ""}
                onChange={(e) => handleCompanyChange("name", e.target.value)}
                style={{ padding: "10px", borderRadius: "8px", border: "1px solid #ddd" }}
              />
              <input
                type="tel"
                placeholder="Phone Number"
                value={editedCompany.phone || ""}
                onChange={(e) => handleCompanyChange("phone", e.target.value)}
                style={{ padding: "10px", borderRadius: "8px", border: "1px solid #ddd" }}
              />
              <textarea
                placeholder="Address"
                value={editedCompany.address || ""}
                onChange={(e) => handleCompanyChange("address", e.target.value)}
                rows="3"
                style={{ padding: "10px", borderRadius: "8px", border: "1px solid #ddd" }}
              />
              <input
                type="text"
                placeholder="GSTIN Number"
                value={editedCompany.gstin || ""}
                onChange={(e) => handleCompanyChange("gstin", e.target.value.toUpperCase())}
                style={{ padding: "10px", borderRadius: "8px", border: "1px solid #ddd" }}
              />
              <input
                type="email"
                placeholder="Email"
                value={editedCompany.email || ""}
                onChange={(e) => handleCompanyChange("email", e.target.value)}
                style={{ padding: "10px", borderRadius: "8px", border: "1px solid #ddd" }}
              />
              <input
                type="text"
                placeholder="Website"
                value={editedCompany.website || ""}
                onChange={(e) => handleCompanyChange("website", e.target.value)}
                style={{ padding: "10px", borderRadius: "8px", border: "1px solid #ddd" }}
              />
              <input
                type="text"
                placeholder="State"
                value={editedCompany.state || ""}
                onChange={(e) => handleCompanyChange("state", e.target.value)}
                style={{ padding: "10px", borderRadius: "8px", border: "1px solid #ddd" }}
              />
              <input
                type="text"
                placeholder="State Code"
                value={editedCompany.stateCode || ""}
                onChange={(e) => handleCompanyChange("stateCode", e.target.value)}
                style={{ padding: "10px", borderRadius: "8px", border: "1px solid #ddd" }}
              />
              <button
                onClick={saveCompanyDetails}
                style={{
                  background: "#4CAF50",
                  color: "white",
                  border: "none",
                  padding: "12px",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "bold",
                  marginTop: "5px"
                }}
              >
                Save Company Details
              </button>
            </div>
          ) : (
            // View Mode
            <div>
              <div className="list-item">
                <span>Company Name:</span>
                <span style={{ fontWeight: "bold" }}>{company.name || "Not set"}</span>
              </div>
              <div className="list-item">
                <span>Phone:</span>
                <span>{company.phone || "Not set"}</span>
              </div>
              <div className="list-item">
                <span>Address:</span>
                <span>{company.address || "Not set"}</span>
              </div>
              <div className="list-item">
                <span>GSTIN:</span>
                <span style={{ textTransform: "uppercase" }}>{company.gstin || "Not set"}</span>
              </div>
              <div className="list-item">
                <span>Email:</span>
                <span>{company.email || "Not set"}</span>
              </div>
              <div className="list-item">
                <span>Website:</span>
                <span>{company.website || "Not set"}</span>
              </div>
              <div className="list-item">
                <span>State:</span>
                <span>{company.state || "Not set"}</span>
              </div>
              <div className="list-item">
                <span>State Code:</span>
                <span>{company.stateCode || "Not set"}</span>
              </div>
            </div>
          )
        ) : (
          // No Company Details - Show Add Option
          <div style={{ textAlign: "center", padding: "20px", background: "#f8f9fa", borderRadius: "8px" }}>
            <p style={{ color: "#666", marginBottom: "15px" }}>
              No company details added yet. Add your company information to appear on invoices and bills.
            </p>
            <button
              onClick={() => setShowAddCompanyModal(true)}
              style={{
                background: "#5a60ea",
                color: "white",
                border: "none",
                padding: "12px 25px",
                borderRadius: "25px",
                cursor: "pointer",
                fontSize: "14px",
                fontWeight: "bold"
              }}
            >
              + Add Company Details
            </button>
          </div>
        )}
      </div>

      {/* BANK DETAILS SECTION */}
      <div className="section-card">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "15px" }}>
          <h3 style={{ margin: 0 }}>Bank Details</h3>
          <button
            onClick={handleBankEditToggle}
            style={{
              background: isEditingBank ? "#ff4444" : "#5a60ea",
              color: "white",
              border: "none",
              padding: "5px 15px",
              borderRadius: "20px",
              cursor: "pointer",
              fontSize: "12px"
            }}
          >
            {isEditingBank ? "Cancel" : hasBankDetails ? "Edit" : "Add"}
          </button>
        </div>

        {isEditingBank ? (
          // Edit Bank Mode
          <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            <input
              type="text"
              placeholder="Bank Name"
              value={editedBankDetails.bankName || ""}
              onChange={(e) => handleBankChange("bankName", e.target.value)}
              style={{ padding: "10px", borderRadius: "8px", border: "1px solid #ddd" }}
            />
            <input
              type="text"
              placeholder="Account Number"
              value={editedBankDetails.accountNo || ""}
              onChange={(e) => handleBankChange("accountNo", e.target.value)}
              style={{ padding: "10px", borderRadius: "8px", border: "1px solid #ddd" }}
            />
            <input
              type="text"
              placeholder="IFSC Code"
              value={editedBankDetails.ifsc || ""}
              onChange={(e) => handleBankChange("ifsc", e.target.value.toUpperCase())}
              style={{ padding: "10px", borderRadius: "8px", border: "1px solid #ddd", textTransform: "uppercase" }}
            />
            <input
              type="text"
              placeholder="Account Holder Name"
              value={editedBankDetails.holderName || ""}
              onChange={(e) => handleBankChange("holderName", e.target.value)}
              style={{ padding: "10px", borderRadius: "8px", border: "1px solid #ddd" }}
            />
            <input
              type="text"
              placeholder="Branch Name"
              value={editedBankDetails.branch || ""}
              onChange={(e) => handleBankChange("branch", e.target.value)}
              style={{ padding: "10px", borderRadius: "8px", border: "1px solid #ddd" }}
            />
            <button
              onClick={saveBankDetails}
              style={{
                background: "#4CAF50",
                color: "white",
                border: "none",
                padding: "12px",
                borderRadius: "8px",
                cursor: "pointer",
                fontSize: "14px",
                fontWeight: "bold",
                marginTop: "5px"
              }}
            >
              Save Bank Details
            </button>
          </div>
        ) : (
          // View Bank Mode
          <div>
            {hasBankDetails ? (
              <>
                <div className="list-item">
                  <span>Bank Name:</span>
                  <span>{bankDetails.bankName || "Not set"}</span>
                </div>
                <div className="list-item">
                  <span>Account Number:</span>
                  <span>{bankDetails.accountNo ? `xxxx${bankDetails.accountNo.slice(-4)}` : "Not set"}</span>
                </div>
                <div className="list-item">
                  <span>IFSC Code:</span>
                  <span>{bankDetails.ifsc || "Not set"}</span>
                </div>
                <div className="list-item">
                  <span>Account Holder:</span>
                  <span>{bankDetails.holderName || "Not set"}</span>
                </div>
                <div className="list-item">
                  <span>Branch:</span>
                  <span>{bankDetails.branch || "Not set"}</span>
                </div>
              </>
            ) : (
              <div style={{ textAlign: "center", padding: "20px", background: "#f8f9fa", borderRadius: "8px" }}>
                <p style={{ color: "#666", marginBottom: "10px" }}>
                  No bank details added yet. Add bank details to appear on invoices.
                </p>
                <button
                  onClick={handleBankEditToggle}
                  style={{
                    background: "#5a60ea",
                    color: "white",
                    border: "none",
                    padding: "10px 20px",
                    borderRadius: "20px",
                    cursor: "pointer",
                    fontSize: "13px"
                  }}
                >
                  + Add Bank Details
                </button>
              </div>
            )}
          </div>
        )}
        <p style={{ fontSize: "11px", color: "#888", marginTop: "10px" }}>
          Bank details will appear on invoices for bank transfer payments
        </p>
      </div>

      {/* UPI QR CODE SECTION - WITH REAL QR CODE PREVIEW */}
      <div className="section-card">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "15px" }}>
          <h3 style={{ margin: 0 }}>UPI Payment QR Code</h3>
          <button
            onClick={handleUPIEditToggle}
            style={{
              background: isEditingUPI ? "#ff4444" : "#5a60ea",
              color: "white",
              border: "none",
              padding: "5px 15px",
              borderRadius: "20px",
              cursor: "pointer",
              fontSize: "12px"
            }}
          >
            {isEditingUPI ? "Cancel" : hasUPIDetails ? "Edit" : "Add"}
          </button>
        </div>

        {isEditingUPI ? (
          // Edit UPI Mode
          <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            <input
              type="text"
              placeholder="UPI ID (e.g., name@bank)"
              value={editedUPIDetails.upiId || ""}
              onChange={(e) => handleUPIChange("upiId", e.target.value.toLowerCase())}
              style={{ padding: "10px", borderRadius: "8px", border: "1px solid #ddd" }}
            />
            <select
              value={editedUPIDetails.upiApp || ""}
              onChange={(e) => handleUPIChange("upiApp", e.target.value)}
              style={{ padding: "10px", borderRadius: "8px", border: "1px solid #ddd" }}
            >
              <option value="">Select UPI App (Optional)</option>
              <option value="Google Pay">Google Pay</option>
              <option value="PhonePe">PhonePe</option>
              <option value="Paytm">Paytm</option>
              <option value="BHIM">BHIM</option>
              <option value="Amazon Pay">Amazon Pay</option>
              <option value="Other">Other</option>
            </select>
            
            <div style={{ display: "flex", alignItems: "center", gap: "10px", margin: "10px 0" }}>
              <input
                type="checkbox"
                id="showQR"
                checked={editedUPIDetails.showQR}
                onChange={(e) => handleUPIChange("showQR", e.target.checked)}
              />
              <label htmlFor="showQR" style={{ fontSize: "14px" }}>
                Show QR Code on invoices
              </label>
            </div>

            {/* Real-time QR Code Preview */}
            {editedUPIDetails.upiId && (
              <div style={{ margin: "15px 0", textAlign: "center" }}>
                <p style={{ fontSize: "13px", color: "#666", marginBottom: "10px" }}>
                  <strong>Live Preview:</strong> Scan this QR code to pay ₹100 (sample amount)
                </p>
                {isGeneratingQR ? (
                  <div style={{
                    width: "200px",
                    height: "200px",
                    margin: "0 auto",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "#f5f5f5",
                    borderRadius: "10px"
                  }}>
                    <p>Generating QR...</p>
                  </div>
                ) : qrCodePreview ? (
                  <div style={{
                    padding: "15px",
                    background: "#f8f9fa",
                    borderRadius: "10px",
                    display: "inline-block"
                  }}>
                    <img 
                      src={qrCodePreview} 
                      alt="UPI QR Code Preview" 
                      style={{
                        width: "200px",
                        height: "200px",
                        display: "block",
                        margin: "0 auto",
                        border: "1px solid #ddd",
                        padding: "5px",
                        background: "white"
                      }}
                    />
                    <p style={{ fontSize: "12px", color: "#5a60ea", marginTop: "10px" }}>
                      UPI ID: {editedUPIDetails.upiId}
                    </p>
                  </div>
                ) : (
                  <div style={{
                    width: "200px",
                    height: "200px",
                    margin: "0 auto",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "#f5f5f5",
                    borderRadius: "10px",
                    color: "#999"
                  }}>
                    Invalid UPI ID
                  </div>
                )}
              </div>
            )}

            <button
              onClick={saveUPIDetails}
              style={{
                background: "#4CAF50",
                color: "white",
                border: "none",
                padding: "12px",
                borderRadius: "8px",
                cursor: "pointer",
                fontSize: "14px",
                fontWeight: "bold",
                marginTop: "5px"
              }}
            >
              Save UPI Details
            </button>
          </div>
        ) : (
          // View UPI Mode
          <div>
            {hasUPIDetails ? (
              <>
                <div className="list-item">
                  <span>UPI ID:</span>
                  <span style={{ fontWeight: "bold", color: "#5a60ea" }}>{upiDetails.upiId}</span>
                </div>
                {upiDetails.upiApp && (
                  <div className="list-item">
                    <span>UPI App:</span>
                    <span>{upiDetails.upiApp}</span>
                  </div>
                )}
                <div className="list-item">
                  <span>Show QR on Invoices:</span>
                  <span>{upiDetails.showQR ? "Yes" : "No"}</span>
                </div>
                
                {/* Show actual QR code preview */}
                {upiDetails.showQR && upiDetails.upiId && qrCodePreview && (
                  <div style={{
                    marginTop: "20px",
                    padding: "15px",
                    background: "#f8f9fa",
                    borderRadius: "10px",
                    textAlign: "center"
                  }}>
                    <p style={{ fontSize: "13px", color: "#666", marginBottom: "10px" }}>
                      <strong>QR Code Preview:</strong> Scan to pay ₹100 (sample)
                    </p>
                    <img 
                      src={qrCodePreview} 
                      alt="UPI QR Code" 
                      style={{
                        width: "180px",
                        height: "180px",
                        display: "block",
                        margin: "0 auto",
                        border: "1px solid #ddd",
                        padding: "5px",
                        background: "white"
                      }}
                    />
                    <p style={{ fontSize: "11px", color: "#888", marginTop: "10px" }}>
                      On actual invoices, QR code will contain the exact invoice amount
                    </p>
                  </div>
                )}
              </>
            ) : (
              <div style={{ textAlign: "center", padding: "20px", background: "#f8f9fa", borderRadius: "8px" }}>
                <p style={{ color: "#666", marginBottom: "10px" }}>
                  No UPI ID added yet. Add UPI ID to enable QR code payments on invoices.
                </p>
                <button
                  onClick={handleUPIEditToggle}
                  style={{
                    background: "#5a60ea",
                    color: "white",
                    border: "none",
                    padding: "10px 20px",
                    borderRadius: "20px",
                    cursor: "pointer",
                    fontSize: "13px"
                  }}
                >
                  + Add UPI ID
                </button>
              </div>
            )}
          </div>
        )}
        <p style={{ fontSize: "11px", color: "#888", marginTop: "10px" }}>
          Customers can scan QR code to pay via UPI apps. Amount will be auto-filled.
        </p>
      </div>

      {/* PASSWORD CHANGE SECTION */}
      <div className="section-card">
        <h3>Security</h3>
        <div className="list-item" onClick={() => setShowPasswordModal(true)}>
          Change Password →
        </div>
      </div>

      {/* FINANCIAL YEAR SECTION */}
      <div className="section-card">
        <h3>Financial Year</h3>
        <div className="list-item" onClick={() => {
          setSelectedFinancialYear(financialYear);
          setShowFinancialYearModal(true);
        }}>
          <span>Current Financial Year:</span>
          <span style={{ fontWeight: "bold", color: "#5a60ea" }}>{financialYear}</span>
        </div>
        <div style={{ fontSize: "12px", color: "#666", marginTop: "5px", padding: "0 10px" }}>
          Financial year runs from 1st April to 31st March
        </div>
      </div>

      {/* BILL SIZE SETTINGS */}
      <div className="section-card">
        <h3>Bill Size Settings</h3>
        
        <div style={{ marginBottom: "15px" }}>
          <label style={{ fontSize: "13px", color: "#666", display: "block", marginBottom: "5px" }}>
            Default Bill Paper Size:
          </label>
          <select
            value={billSize}
            onChange={(e) => setBillSize(e.target.value)}
            style={{
              width: "100%",
              padding: "12px",
              borderRadius: "8px",
              border: "1px solid #ddd",
              fontSize: "14px"
            }}
          >
            {billSizeOptions.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
          <p style={{ fontSize: "11px", color: "#888", marginTop: "5px" }}>
            Select the default paper size for printing invoices
          </p>
        </div>

        <div style={{ marginBottom: "15px" }}>
          <label style={{ fontSize: "13px", color: "#666", display: "block", marginBottom: "5px" }}>
            Thermal Printer Compatibility:
          </label>
          <div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
            <input
              type="checkbox"
              id="thermalMode"
              checked={billSize.includes('thermal')}
              onChange={() => {
                if (billSize.includes('thermal')) {
                  setBillSize('a4');
                } else {
                  setBillSize('thermal-80');
                }
              }}
            />
            <label htmlFor="thermalMode">Enable Thermal Printer Mode</label>
          </div>
        </div>
      </div>

      {/* PRINT FORMAT SETTINGS */}
      <div className="section-card">
        <h3>Print Format Settings</h3>
        
        <div style={{ marginBottom: "15px" }}>
          <label style={{ fontSize: "13px", color: "#666", display: "block", marginBottom: "5px" }}>
            Invoice Print Format:
          </label>
          <select
            value={printFormat}
            onChange={(e) => setPrintFormat(e.target.value)}
            style={{
              width: "100%",
              padding: "12px",
              borderRadius: "8px",
              border: "1px solid #ddd",
              fontSize: "14px"
            }}
          >
            {printFormats.map((format) => (
              <option key={format.value} value={format.value}>{format.label}</option>
            ))}
          </select>
        </div>

        <div style={{ marginBottom: "15px" }}>
          <label style={{ fontSize: "13px", color: "#666", display: "block", marginBottom: "5px" }}>
            Bill Type / Raw Bill Format:
          </label>
          <select
            value={billFormat}
            onChange={(e) => setBillFormat(e.target.value)}
            style={{
              width: "100%",
              padding: "12px",
              borderRadius: "8px",
              border: "1px solid #ddd",
              fontSize: "14px"
            }}
          >
            {billFormats.map((format) => (
              <option key={format.value} value={format.value}>{format.label}</option>
            ))}
          </select>
        </div>

        <div style={{ marginBottom: "15px" }}>
          <label style={{ fontSize: "13px", color: "#666", display: "block", marginBottom: "5px" }}>
            PDF Share Option:
          </label>
          <select
            value={pdfShareOption}
            onChange={(e) => setPdfShareOption(e.target.value)}
            style={{
              width: "100%",
              padding: "12px",
              borderRadius: "8px",
              border: "1px solid #ddd",
              fontSize: "14px"
            }}
          >
            <option value="share">Share as PDF</option>
            <option value="download">Download PDF</option>
            <option value="print">Direct Print</option>
            <option value="whatsapp">Share on WhatsApp</option>
            <option value="email">Send via Email</option>
          </select>
        </div>
      </div>

      {/* NOTIFICATION SETTINGS */}
      <div className="section-card">
        <h3>Notifications</h3>
        
        <div className="list-item" style={{ cursor: "default" }}>
          <span>Enable Push Notifications</span>
          <label className="switch">
            <input
              type="checkbox"
              checked={notificationEnabled}
              onChange={(e) => setNotificationEnabled(e.target.checked)}
            />
            <span className="slider round"></span>
          </label>
        </div>

        <div className="list-item" style={{ cursor: "default" }}>
          <span>Low Stock Alerts</span>
          <label className="switch">
            <input
              type="checkbox"
              checked={true}
              onChange={() => {}}
            />
            <span className="slider round"></span>
          </label>
        </div>

        <div className="list-item" style={{ cursor: "default" }}>
          <span>Daily Backup Notifications</span>
          <label className="switch">
            <input
              type="checkbox"
              checked={autoBackup}
              onChange={(e) => setAutoBackup(e.target.checked)}
            />
            <span className="slider round"></span>
          </label>
        </div>
      </div>

      {/* OTHER SETTINGS */}
      <div className="section-card">
        <h3>Other Settings</h3>
        
        <div className="list-item" style={{ cursor: "default" }}>
          <span>Dark Mode</span>
          <label className="switch">
            <input
              type="checkbox"
              checked={darkMode}
              onChange={(e) => setDarkMode(e.target.checked)}
            />
            <span className="slider round"></span>
          </label>
        </div>

        <div className="list-item" style={{ cursor: "default" }}>
          <span>Auto Backup</span>
          <label className="switch">
            <input
              type="checkbox"
              checked={autoBackup}
              onChange={(e) => setAutoBackup(e.target.checked)}
            />
            <span className="slider round"></span>
          </label>
        </div>

        <div className="list-item">
          <span>Data Backup & Restore</span>
          <span>→</span>
        </div>

        <div className="list-item">
          <span>Export Data (Excel/PDF)</span>
          <span>→</span>
        </div>
      </div>

      {/* SUPPORT SECTION */}
      <div className="section-card">
        <h3>Support</h3>
        <div className="list-item">About Us →</div>
        <div className="list-item">Help & Support →</div>
        <div className="list-item">License & Expiry →</div>
        <div className="list-item">Terms & Conditions →</div>
        <div className="list-item">Privacy Policy →</div>

        <div className="list-item logout" onClick={logout}>
          Logout →
        </div>
      </div>

      {/* SAVE SETTINGS BUTTON */}
      <div style={{ padding: "15px" }}>
        <button
          onClick={saveSettings}
          style={{
            width: "100%",
            background: "linear-gradient(135deg, #5a60ea, #6f5bff)",
            color: "white",
            border: "none",
            padding: "15px",
            borderRadius: "10px",
            fontSize: "16px",
            fontWeight: "bold",
            cursor: "pointer"
          }}
        >
          Save All Settings
        </button>
      </div>

      {/* PASSWORD CHANGE MODAL */}
      {showPasswordModal && (
        <div style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "rgba(0,0,0,0.5)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 2000
        }}>
          <div style={{
            background: "white",
            padding: "20px",
            borderRadius: "12px",
            width: "90%",
            maxWidth: "400px"
          }}>
            <h3 style={{ marginBottom: "20px" }}>Change Password</h3>
            
            <input
              type="password"
              placeholder="Current Password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              style={{
                width: "100%",
                padding: "12px",
                marginBottom: "10px",
                borderRadius: "8px",
                border: "1px solid #ddd"
              }}
            />
            
            <input
              type="password"
              placeholder="New Password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              style={{
                width: "100%",
                padding: "12px",
                marginBottom: "10px",
                borderRadius: "8px",
                border: "1px solid #ddd"
              }}
            />
            
            <input
              type="password"
              placeholder="Confirm New Password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              style={{
                width: "100%",
                padding: "12px",
                marginBottom: "10px",
                borderRadius: "8px",
                border: "1px solid #ddd"
              }}
            />

            {passwordError && (
              <p style={{ color: "#ff4444", fontSize: "12px", marginBottom: "10px" }}>
                {passwordError}
              </p>
            )}

            {passwordSuccess && (
              <p style={{ color: "#4CAF50", fontSize: "12px", marginBottom: "10px" }}>
                {passwordSuccess}
              </p>
            )}

            <div style={{ display: "flex", gap: "10px" }}>
              <button
                onClick={() => setShowPasswordModal(false)}
                style={{
                  flex: 1,
                  padding: "12px",
                  background: "#ddd",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer"
                }}
              >
                Cancel
              </button>
              <button
                onClick={handlePasswordChange}
                style={{
                  flex: 1,
                  padding: "12px",
                  background: "#5a60ea",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer"
                }}
              >
                Change Password
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ADD COMPANY MODAL */}
      {showAddCompanyModal && (
        <div style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "rgba(0,0,0,0.5)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 2000,
          overflowY: "auto",
          padding: "20px"
        }}>
          <div style={{
            background: "white",
            padding: "25px",
            borderRadius: "12px",
            width: "90%",
            maxWidth: "500px",
            maxHeight: "90vh",
            overflowY: "auto"
          }}>
            <h3 style={{ marginBottom: "20px", color: "#333" }}>Add Company Details</h3>
            
            <p style={{ fontSize: "13px", color: "#666", marginBottom: "20px" }}>
              Enter your company information. These details will appear on your invoices and bills.
            </p>
            
            <div style={{ display: "flex", flexDirection: "column", gap: "15px" }}>
              <div>
                <label style={{ fontSize: "13px", color: "#666", display: "block", marginBottom: "5px" }}>
                  Company Name <span style={{ color: "#ff4444" }}>*</span>
                </label>
                <input
                  type="text"
                  placeholder="Enter company name"
                  value={newCompany.name}
                  onChange={(e) => handleNewCompanyChange("name", e.target.value)}
                  style={{
                    width: "100%",
                    padding: "12px",
                    borderRadius: "8px",
                    border: "1px solid #ddd",
                    fontSize: "14px"
                  }}
                />
              </div>

              <div>
                <label style={{ fontSize: "13px", color: "#666", display: "block", marginBottom: "5px" }}>
                  Phone Number
                </label>
                <input
                  type="tel"
                  placeholder="Enter phone number"
                  value={newCompany.phone}
                  onChange={(e) => handleNewCompanyChange("phone", e.target.value)}
                  style={{
                    width: "100%",
                    padding: "12px",
                    borderRadius: "8px",
                    border: "1px solid #ddd",
                    fontSize: "14px"
                  }}
                />
              </div>

              <div>
                <label style={{ fontSize: "13px", color: "#666", display: "block", marginBottom: "5px" }}>
                  Address
                </label>
                <textarea
                  placeholder="Enter complete address"
                  value={newCompany.address}
                  onChange={(e) => handleNewCompanyChange("address", e.target.value)}
                  rows="3"
                  style={{
                    width: "100%",
                    padding: "12px",
                    borderRadius: "8px",
                    border: "1px solid #ddd",
                    fontSize: "14px"
                  }}
                />
              </div>

              <div>
                <label style={{ fontSize: "13px", color: "#666", display: "block", marginBottom: "5px" }}>
                  GSTIN Number
                </label>
                <input
                  type="text"
                  placeholder="Enter GSTIN number"
                  value={newCompany.gstin}
                  onChange={(e) => handleNewCompanyChange("gstin", e.target.value.toUpperCase())}
                  style={{
                    width: "100%",
                    padding: "12px",
                    borderRadius: "8px",
                    border: "1px solid #ddd",
                    fontSize: "14px",
                    textTransform: "uppercase"
                  }}
                />
              </div>

              <div>
                <label style={{ fontSize: "13px", color: "#666", display: "block", marginBottom: "5px" }}>
                  Email Address
                </label>
                <input
                  type="email"
                  placeholder="Enter email address"
                  value={newCompany.email}
                  onChange={(e) => handleNewCompanyChange("email", e.target.value)}
                  style={{
                    width: "100%",
                    padding: "12px",
                    borderRadius: "8px",
                    border: "1px solid #ddd",
                    fontSize: "14px"
                  }}
                />
              </div>

              <div>
                <label style={{ fontSize: "13px", color: "#666", display: "block", marginBottom: "5px" }}>
                  Website
                </label>
                <input
                  type="text"
                  placeholder="Enter website URL"
                  value={newCompany.website}
                  onChange={(e) => handleNewCompanyChange("website", e.target.value)}
                  style={{
                    width: "100%",
                    padding: "12px",
                    borderRadius: "8px",
                    border: "1px solid #ddd",
                    fontSize: "14px"
                  }}
                />
              </div>

              <div>
                <label style={{ fontSize: "13px", color: "#666", display: "block", marginBottom: "5px" }}>
                  State
                </label>
                <input
                  type="text"
                  placeholder="Enter state"
                  value={newCompany.state}
                  onChange={(e) => handleNewCompanyChange("state", e.target.value)}
                  style={{
                    width: "100%",
                    padding: "12px",
                    borderRadius: "8px",
                    border: "1px solid #ddd",
                    fontSize: "14px"
                  }}
                />
              </div>

              <div>
                <label style={{ fontSize: "13px", color: "#666", display: "block", marginBottom: "5px" }}>
                  State Code
                </label>
                <input
                  type="text"
                  placeholder="Enter state code"
                  value={newCompany.stateCode}
                  onChange={(e) => handleNewCompanyChange("stateCode", e.target.value)}
                  style={{
                    width: "100%",
                    padding: "12px",
                    borderRadius: "8px",
                    border: "1px solid #ddd",
                    fontSize: "14px"
                  }}
                />
              </div>
            </div>

            <div style={{ display: "flex", gap: "10px", marginTop: "25px" }}>
              <button
                onClick={() => setShowAddCompanyModal(false)}
                style={{
                  flex: 1,
                  padding: "14px",
                  background: "#ddd",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "bold"
                }}
              >
                Cancel
              </button>
              <button
                onClick={addCompany}
                style={{
                  flex: 1,
                  padding: "14px",
                  background: "#4CAF50",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: "bold"
                }}
              >
                Add Company
              </button>
            </div>
          </div>
        </div>
      )}

      {/* FINANCIAL YEAR CHANGE MODAL */}
      {showFinancialYearModal && (
        <div style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: "rgba(0,0,0,0.5)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 2000
        }}>
          <div style={{
            background: "white",
            padding: "20px",
            borderRadius: "12px",
            width: "90%",
            maxWidth: "400px"
          }}>
            <h3 style={{ marginBottom: "10px" }}>Change Financial Year</h3>
            
            <p style={{ fontSize: "13px", color: "#666", marginBottom: "15px" }}>
              Select financial year (1st April - 31st March)
            </p>
            
            <select
              value={selectedFinancialYear}
              onChange={(e) => setSelectedFinancialYear(e.target.value)}
              style={{
                width: "100%",
                padding: "12px",
                marginBottom: "15px",
                borderRadius: "8px",
                border: "1px solid #ddd",
                fontSize: "14px"
              }}
            >
              {financialYearOptions.map((year) => (
                <option key={year.value} value={year.value}>
                  {year.label}
                </option>
              ))}
            </select>

            {financialYearError && (
              <p style={{ color: "#ff4444", fontSize: "12px", marginBottom: "10px" }}>
                {financialYearError}
              </p>
            )}

            {financialYearSuccess && (
              <p style={{ color: "#4CAF50", fontSize: "12px", marginBottom: "10px" }}>
                {financialYearSuccess}
              </p>
            )}

            <div style={{ display: "flex", gap: "10px" }}>
              <button
                onClick={() => setShowFinancialYearModal(false)}
                style={{
                  flex: 1,
                  padding: "12px",
                  background: "#ddd",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer"
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleFinancialYearChange}
                style={{
                  flex: 1,
                  padding: "12px",
                  background: "#5a60ea",
                  color: "white",
                  border: "none",
                  borderRadius: "8px",
                  cursor: "pointer"
                }}
              >
                Change Year
              </button>
            </div>
          </div>
        </div>
      )}
{/* ================= BOTTOM NAV ================= */}
      <div className="bottom-nav">
        <div onClick={() => navigate("/")} className="nav-item active">
          🏠
          <p>Home</p>
        </div>
        <div onClick={() => navigate("/items")} className="nav-item">
          📦
          <p>Items</p>
        </div>
        <div onClick={() => navigate("/customers")} className="nav-item">
          👥
          <p>Masters</p>
        </div>
        <div onClick={() => navigate("/invoicelist")} className="nav-item">
          🧾
          <p>Invoices</p>
        </div>
      </div>
      {/* Add CSS for toggle switches */}
      <style>{`
        .switch {
          position: relative;
          display: inline-block;
          width: 50px;
          height: 24px;
        }
        .switch input {
          opacity: 0;
          width: 0;
          height: 0;
        }
        .slider {
          position: absolute;
          cursor: pointer;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background-color: #ccc;
          transition: .4s;
        }
        .slider:before {
          position: absolute;
          content: "";
          height: 20px;
          width: 20px;
          left: 2px;
          bottom: 2px;
          background-color: white;
          transition: .4s;
        }
        input:checked + .slider {
          background-color: #5a60ea;
        }
        input:checked + .slider:before {
          transform: translateX(26px);
        }
        .slider.round {
          border-radius: 24px;
        }
        .slider.round:before {
          border-radius: 50%;
        }

        .list-item {
          padding: 12px 15px;
          border-bottom: 1px solid #f0f0f0;
          display: flex;
          justify-content: space-between;
          align-items: center;
          cursor: pointer;
        }

        .list-item.logout {
          color: #ff4444;
        }

        .section-card {
          background: white;
          border-radius: 12px;
          margin: 10px 15px;
          padding: 15px;
          box-shadow: 0 2px 4px rgba(0,0,0,0.05);
        }

        .page-header {
          padding: 15px;
          background: white;
          border-bottom: 1px solid #eee;
          margin-bottom: 10px;
        }

        .bottom-nav {
          position: fixed;
          bottom: 0;
          left: 0;
          right: 0;
          background: white;
          display: flex;
          justify-content: space-around;
          padding: 8px 0;
          box-shadow: 0 -2px 10px rgba(0,0,0,0.05);
          z-index: 100;
        }

        .bottom-nav div {
          text-align: center;
          cursor: pointer;
          color: #999;
        }

        .bottom-nav p {
          margin: 2px 0 0;
          font-size: 11px;
        }
      `}</style>

    </div>
  );
}